// Binary form of a snapshot (race-net.ts): a fixed 12-byte record per rider for the hot part, then the cold records,
// hazards, missiles and crates. A 50-rider snapshot weighs about 700 bytes against 7 KB of JSON. Little-endian.
//
//   header   magic 'G' u8 · version u8 · flags u8 (bit 0: full) · state u8 · t f64 · elimLap u16 · counts u16 ×5
//   hot      s f32 · lane i16 (×1000) · v u16 (×100 m/s) · power u16 · cadence u8 · draft u8 (×100)
//   cold     index u16 · gear u8 · item str · rolling u8 · effects u8 + (key str · end f32)… · shield end f32 ·
//            invulnerable u8 · out u8 · finish f32 (NaN: none) · reserved u8 (0: stored replays carry this byte) · hits u16 · out order u16 · human u8 · bomb f32
//   hazards  s f32 · lane f32 · item str          missiles  s f32 · lane f32 · item str          crates  u8 each
//   str      length u8 · UTF-8 bytes (empty: none)
//
// The decoder trusts nothing: counts are bounded, every read is inside the buffer, nothing may be left over.
import type { ColdState, RaceState, Snapshot } from './race-types.js';
import type { ItemKey } from './items.js';

export const SNAP_MAGIC = 0x47, SNAP_VERSION = 1;
const STATES: RaceState[] = ['countdown', 'running', 'done'];
/** Bounds of a sane snapshot: a field of MAX_RIDERS, and more hazards than a race can hold. */
const MAX = { riders: 255, cold: 255, hazards: 2000, shells: 500, boxes: 2000, fx: 32, str: 255 };

const enc = new TextEncoder(), dec = new TextDecoder('utf-8', { fatal: true });

class Writer {
  private buf = new Uint8Array(2048); private view = new DataView(this.buf.buffer); pos = 0;
  private room(n: number) {
    if (this.pos + n <= this.buf.length) return;
    const b = new Uint8Array(Math.max(this.buf.length * 2, this.pos + n)); b.set(this.buf); this.buf = b; this.view = new DataView(b.buffer);
  }
  u8(v: number) { this.room(1); this.view.setUint8(this.pos, clampInt(v, 0, 255)); this.pos += 1; }
  u16(v: number) { this.room(2); this.view.setUint16(this.pos, clampInt(v, 0, 65535), true); this.pos += 2; }
  i16(v: number) { this.room(2); this.view.setInt16(this.pos, clampInt(v, -32768, 32767), true); this.pos += 2; }
  f32(v: number) { this.room(4); this.view.setFloat32(this.pos, v, true); this.pos += 4; }
  f64(v: number) { this.room(8); this.view.setFloat64(this.pos, v, true); this.pos += 8; }
  str(s: string | null | undefined) { const b = s ? enc.encode(s).subarray(0, MAX.str) : new Uint8Array(0); this.u8(b.length); this.room(b.length); this.buf.set(b, this.pos); this.pos += b.length; }
  done(): ArrayBuffer { return this.buf.slice(0, this.pos).buffer; }
}
const clampInt = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(Number.isFinite(v) ? v : 0)));

export function encodeSnapshot(S: Snapshot): ArrayBuffer {
  const w = new Writer(), cold = Object.entries(S.c).slice(0, MAX.cold);
  w.u8(SNAP_MAGIC); w.u8(SNAP_VERSION); w.u8(S.f ? 1 : 0); w.u8(Math.max(0, STATES.indexOf(S.st)));
  w.f64(S.t); w.u16(S.el);
  w.u16(S.r.length); w.u16(cold.length); w.u16(S.h.length); w.u16(S.sh.length); w.u16(S.b.length);
  for (const d of S.r) { w.f32(d[0]); w.i16(d[1] * 1000); w.u16(d[2] * 100); w.u16(d[3]); w.u8(d[4]); w.u8(d[5] * 100); }
  for (const [k, d] of cold) {
    const fx = Object.entries(d[3]).slice(0, MAX.fx);
    w.u16(+k); w.u8(d[0]); w.str(d[1]); w.u8(d[2]); w.u8(fx.length);
    for (const [key, end] of fx) { w.str(key); w.f32(end); }
    w.f32(d[4] == null ? NaN : d[4]); w.u8(d[5]); w.u8(d[6]); w.f32(d[7] == null ? NaN : d[7]); w.u8(0); w.u16(d[8]); w.u16(d[9]); w.u8(d[10]); w.f32(d[11]);
  }
  for (const [s, lane, item] of S.h) { w.f32(s); w.f32(lane); w.str(item); }
  for (const [s, lane, item] of S.sh) { w.f32(s); w.f32(lane); w.str(item); }
  for (const b of S.b) w.u8(b);
  return w.done();
}

class Reader {
  private view: DataView; pos = 0;
  constructor(private buf: Uint8Array) { this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength); }
  get left() { return this.buf.byteLength - this.pos; }
  u8() { const v = this.view.getUint8(this.pos); this.pos += 1; return v; }
  u16() { const v = this.view.getUint16(this.pos, true); this.pos += 2; return v; }
  i16() { const v = this.view.getInt16(this.pos, true); this.pos += 2; return v; }
  f32() { const v = this.view.getFloat32(this.pos, true); this.pos += 4; return v; }
  f64() { const v = this.view.getFloat64(this.pos, true); this.pos += 8; return v; }
  str(): string | null { const n = this.u8(); if (n > this.left) throw new RangeError('string'); const s = n ? dec.decode(this.buf.subarray(this.pos, this.pos + n)) : null; this.pos += n; return s; }
}
const count = (n: number, max: number) => { if (n > max) throw new RangeError('count'); return n; };
const finite = (v: number) => { if (!Number.isFinite(v)) throw new RangeError('number'); return v; };

/** Throws `Error('bad snapshot')` on anything that is not a snapshot this version wrote. */
export function decodeSnapshot(data: ArrayBuffer | Uint8Array): Snapshot {
  try {
    const r = new Reader(data instanceof Uint8Array ? data : new Uint8Array(data));
    if (r.u8() !== SNAP_MAGIC || r.u8() !== SNAP_VERSION) throw new RangeError('magic');
    const flags = r.u8(), st = STATES[r.u8()];
    if (!st) throw new RangeError('state');
    const t = finite(r.f64()), el = r.u16();
    const nr = count(r.u16(), MAX.riders), nc = count(r.u16(), MAX.cold), nh = count(r.u16(), MAX.hazards), ns = count(r.u16(), MAX.shells), nb = count(r.u16(), MAX.boxes);
    const S: Snapshot = { t, st, el, ...(flags & 1 ? { f: 1 as const } : {}), r: [], c: {}, h: [], sh: [], b: [] };
    for (let i = 0; i < nr; i++) S.r.push([finite(r.f32()), r.i16() / 1000, r.u16() / 100, r.u16(), r.u8(), r.u8() / 100]);
    for (let i = 0; i < nc; i++) {
      const idx = r.u16(); if (idx >= nr) throw new RangeError('index');
      const gear = r.u8(), item = r.str() as ItemKey | null, rolling = r.u8(), nfx = count(r.u8(), MAX.fx), fx: Record<string, number> = {};
      for (let k = 0; k < nfx; k++) { const key = r.str(); if (!key) throw new RangeError('effect'); fx[key] = finite(r.f32()); }
      const shieldRaw = r.f32(), shield = Number.isNaN(shieldRaw) ? null : finite(shieldRaw), invuln = r.u8(), out = r.u8(), fin = r.f32();
      r.u8(); // reserved
      const hits = r.u16(), order = r.u16(), human = r.u8(), bomb = finite(r.f32());
      const cs: ColdState = [gear, item, rolling, fx, shield, invuln, out, Number.isNaN(fin) ? null : finite(fin), hits, order, human, bomb];
      S.c[idx] = cs;
    }
    for (let i = 0; i < nh; i++) { const s = finite(r.f32()), lane = finite(r.f32()), item = r.str(); S.h.push(item ? [s, lane, item] : [s, lane]); }
    for (let i = 0; i < ns; i++) { const s = finite(r.f32()), lane = finite(r.f32()), item = r.str(); S.sh.push(item ? [s, lane, item] : [s, lane]); }
    for (let i = 0; i < nb; i++) S.b.push(r.u8() ? 1 : 0);
    if (r.left !== 0) throw new RangeError('trailing');
    return S;
  } catch {
    throw new Error('bad snapshot');
  }
}
